import { useCallback, useMemo, useState } from "react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useTagGroups } from "../../hooks/useTagGroups";
import { useMultiGroupTagFilter } from "../../hooks/useMultiGroupTagFilter";
import { VERIFIED_FILTER_ID } from "../../utils/parseTagFilters";
import type { DocumentFilterControlProps } from "./DocumentFilterControl";
import {
  countSelectedRows,
  normalizeDerivedParentIds,
  withoutLockedProjectFilters,
} from "./documentFilterRowUtils";

function useDocumentFilterState(props: DocumentFilterControlProps) {
  const { documents, projects = [], activeFilters, onFilterChange, availableFilters } = props;
  const isProjectLocked = props.lockedProjectId != null;
  const lockedProject = isProjectLocked
    ? projects.find((project) => String(project.id) === String(props.lockedProjectId))
    : undefined;
  const tagGroups = useTagGroups({ documents, projects, availableFilters });
  const effectiveActiveFilters = useMemo(
    () =>
      normalizeDerivedParentIds(
        withoutLockedProjectFilters(activeFilters, isProjectLocked),
        tagGroups
      ),
    [activeFilters, isProjectLocked, tagGroups]
  );
  const filterActions = useMultiGroupTagFilter({
    initialFilters: effectiveActiveFilters,
    onFilterChange: (next) => onFilterChange(withoutLockedProjectFilters(next, isProjectLocked)),
    tagGroups,
  });
  const { activeFilters: currentFilters, setFilters } = filterActions;
  const clearGroupFilters = useCallback(
    (groupId: string) => {
      const group = tagGroups.find((candidate) => candidate.id === groupId);
      if (!group) return;
      const ids = new Set(group.tags.flatMap((tag) => [tag.id, ...(tag.childTagIds ?? [])]));
      setFilters(currentFilters.filter((filterId) => !ids.has(filterId)));
    },
    [currentFilters, setFilters, tagGroups]
  );
  const activeFilterCount = useMemo(
    () => countSelectedRows(currentFilters, tagGroups),
    [currentFilters, tagGroups]
  );

  return {
    isProjectLocked,
    lockedProject,
    tagGroups,
    effectiveActiveFilters,
    clearGroupFilters,
    activeFilterCount,
    isEmpty: tagGroups.every((group) => group.tags.length === 0),
    ...filterActions,
  };
}

export function useDocumentFilterControlModel(props: DocumentFilterControlProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const state = useDocumentFilterState(props);

  return {
    className: props.className,
    t,
    f: t.documents.tagFilter,
    open,
    setOpen,
    onOpen: props.onOpen,
    searchQuery,
    setSearchQuery,
    // Any active filter, counted or not: an ID whose row this scope no longer
    // offers still narrows the list, so Clear-all has to stay reachable to
    // undo it — `activeFilterCount` only counts rows the panel can show.
    hasActiveFilters: state.activeFilters.length > 0,
    isVerifiedOnly: state.activeFilters.includes(VERIFIED_FILTER_ID),
    isPending: (props.isLoading ?? false) && state.isEmpty,
    isFailed: !(props.isLoading ?? false) && (props.hasError ?? false) && state.isEmpty,
    ...state,
  };
}

export type DocumentFilterControlModel = ReturnType<typeof useDocumentFilterControlModel>;
